/**
 * Each agent has its own log (owner, 08/10/2026), in both rooms: a click on an agent in the room shows that agent's
 * session in the panels; the person, or "Todos", shows everything their agents did together, each line saying which
 * agent it was. With one agent, that agent. Shared by the follow-up room (pages/Floor.tsx) and the bug room
 * (pages/BugHunt.tsx), so the two never drift apart.
 */

import { useCallback, useMemo, useState, type MouseEvent } from 'react'
import { useQueries } from '@tanstack/react-query'
import { api } from '../lib/api'
import { useI18n } from '../lib/i18n'
import type { WorkSessionDetail } from '../lib/types'
import { agentLookFor, modelOf, parsePreference } from './agentLooks'

/** Agents shown as tabs before the rest go into one list (docs/refactor/25, phase 4). */
const TABS = 4

interface AgentSession {
  workSessionId: string
  activityState: string
  providerName: string | null
}

export function useAgentLog<S extends AgentSession>({
  personId,
  sessions: everySession,
  agentLooks,
  enabled,
}: {
  personId: string | undefined
  sessions: S[]
  agentLooks: string | null | undefined
  enabled: boolean
}) {
  const { t } = useI18n()
  const [pick, setPick] = useState<{ developerId: string; sessionId: string } | null>(null)
  // The agents at work now — the ones the room draws; a quiet session from hours ago is not an agent to pick.
  const sessions = useMemo(() => everySession.filter((session) => session.activityState !== 'Idle'), [everySession])
  const picked = pick && personId && pick.developerId === personId ? sessions.find((s) => s.workSessionId === pick.sessionId) : undefined
  const all = !picked && sessions.length > 1

  const preference = useMemo(() => parsePreference(agentLooks ?? null), [agentLooks])
  const label = useCallback(
    (session: S) => `${modelOf(agentLookFor(session.workSessionId, preference)).name}${session.providerName ? ` · ${session.providerName}` : ''}`,
    [preference],
  )

  // The person's whole log: every agent's events together.
  const details = useQueries({
    queries: (all ? sessions : []).map((session) => ({
      queryKey: ['work', session.workSessionId],
      queryFn: () => api.sessions.detail(session.workSessionId),
      enabled,
      refetchInterval: 10_000,
    })),
  })

  /** The detail the log shows: the one session's, or every agent's timeline merged by time. */
  const merge = useCallback(
    (base: WorkSessionDetail | undefined): WorkSessionDetail | undefined => {
      if (!all) return base
      const timeline = sessions
        .flatMap((session, index) => {
          const detail = details[index]?.data
          if (!detail) return []
          const who = label(session)
          return detail.timeline.map((entry) => ({ ...entry, summary: entry.summary ? `${who} — ${entry.summary}` : entry.summary }))
        })
        .sort((a, b) => a.occurredAt.localeCompare(b.occurredAt))
      // The panel's own session can be past the plan's days of history (gone): any agent's detail carries the rest.
      const carrier = base ?? details.find((query) => query.data)?.data
      return carrier ? { ...carrier, timeline } : undefined
    },
    [all, sessions, details, label],
  )

  /** In the room's click capture: a click on an agent picks its log, a click on the person shows them all. */
  const pickFromClick = useCallback((event: MouseEvent) => {
    const target = event.target as Element
    const station = target.closest?.('[data-drop-developer]')
    if (!station) return
    const sessionId = target.closest?.('[data-agent-session]')?.getAttribute('data-agent-session')
    setPick(sessionId ? { developerId: station.getAttribute('data-drop-developer')!, sessionId } : null)
  }, [])

  const switcher =
    personId && sessions.length > 1 ? (
      <div className="row row--tight row--wrap floor__agents" role="tablist" aria-label={t('room.agentsLog')}>
        <button type="button" role="tab" aria-selected={!picked} className={`btn btn--sm ${!picked ? 'btn--primary' : 'btn--ghost'}`} onClick={() => setPick(null)}>
          {t('room.allAgents', { count: sessions.length })}
        </button>
        {sessions.slice(0, TABS).map((session) => {
          const on = picked?.workSessionId === session.workSessionId
          return (
            <button
              key={session.workSessionId}
              type="button"
              role="tab"
              aria-selected={on}
              className={`btn btn--sm ${on ? 'btn--primary' : 'btn--ghost'}`}
              onClick={() => setPick({ developerId: personId, sessionId: session.workSessionId })}
            >
              {label(session)}
            </button>
          )
        })}
        {/* Many agents (phase 4): the rest in one list instead of a wall of tabs. */}
        {sessions.length > TABS && (
          <select
            className={`input input--sm floor__agents-more${picked && sessions.indexOf(picked) >= TABS ? ' is-active' : ''}`}
            aria-label={t('room.moreAgents', { count: sessions.length - TABS })}
            value={picked && sessions.indexOf(picked) >= TABS ? picked.workSessionId : ''}
            onChange={(event) => event.target.value && setPick({ developerId: personId, sessionId: event.target.value })}
          >
            <option value="">{t('room.moreAgents', { count: sessions.length - TABS })}</option>
            {sessions.slice(TABS).map((session) => (
              <option key={session.workSessionId} value={session.workSessionId}>{label(session)}</option>
            ))}
          </select>
        )}
      </div>
    ) : null

  return { picked, all, merge, pickFromClick, switcher }
}
